import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Sparkles, Star, Crown, Zap, Gift, PartyPopper } from 'lucide-react';
import { useTheme } from './ThemeToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============================================
// 🏆 WINNERS SHOWCASE - Preuve Sociale Spectaculaire
// ============================================

// Prénoms français aléatoires pour fallback
const FRENCH_NAMES = [
  'Sophie', 'Marie', 'Lucas', 'Emma', 'Hugo', 'Léa', 'Thomas', 'Chloé',
  'Nathan', 'Manon', 'Théo', 'Camille', 'Mathis', 'Jade', 'Enzo', 'Louise',
  'Raphaël', 'Sarah', 'Louis', 'Inès', 'Gabriel', 'Léna', 'Jules', 'Anna',
  'Adam', 'Clara', 'Arthur', 'Alice', 'Paul', 'Zoé', 'Victor', 'Lola'
];

const GAME_NAMES = {
  wheel: { name: 'Roue Fortune', emoji: '🎡', color: '#FFD700' },
  jackpot: { name: 'Jackpot', emoji: '🎰', color: '#FF6B6B' },
  dice: { name: 'Dés Cosmiques', emoji: '🎲', color: '#A855F7' },
  chest: { name: 'Coffre Mystère', emoji: '🎴', color: '#F59E0B' },
  target: { name: 'Cible', emoji: '🎯', color: '#EF4444' },
  diamond: { name: 'Diamant', emoji: '💎', color: '#06B6D4' },
  cards: { name: 'Bon Choix', emoji: '🃏', color: '#EC4899' },
  scratch: { name: 'Grattage', emoji: '🎫', color: '#22C55E' },
  snake: { name: 'Snake', emoji: '🐍', color: '#10B981' }
};

// Formater le temps écoulé
const formatTimeAgo = (date) => {
  if (!date) return '';
  const now = new Date();
  const played = new Date(date);
  const diffMs = now - played;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 2) return 'à l\'instant';
  if (diffMins < 60) return `il y a ${diffMins} min`;
  if (diffHours < 24) return `il y a ${diffHours}h`;
  if (diffDays === 1) return 'hier';
  return `il y a ${diffDays}j`;
};

// Générer des gagnants fictifs comme fallback
const generateFallbackWinners = (count = 8) => {
  const winners = [];
  const gameKeys = Object.keys(GAME_NAMES);
  
  for (let i = 0; i < count; i++) {
    const gameKey = gameKeys[Math.floor(Math.random() * gameKeys.length)];
    winners.push({
      id: `fallback-${i}`,
      name: FRENCH_NAMES[Math.floor(Math.random() * FRENCH_NAMES.length)],
      game: gameKey,
      prizeType: 'stamp',
      playedAt: new Date(Date.now() - i * 3600000).toISOString()
    });
  }
  return winners;
};

// ============== COMPOSANT PRINCIPAL ==============
export function WinnersShowcase() {
  const [winners, setWinners] = useState([]);
  const [weeklyCount, setWeeklyCount] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [latestWinner, setLatestWinner] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const previousWinnersRef = useRef([]);
  const { isDarkMode } = useTheme();

  // Charger les vrais gagnants depuis l'API
  const fetchWinners = async () => {
    try {
      const res = await fetch(`${API_URL}/api/games/recent-winners?limit=15`);
      if (res.ok) {
        const data = await res.json();
        const newWinners = data.winners || [];
        
        // Vérifier s'il y a un nouveau gagnant (pour l'animation)
        if (previousWinnersRef.current.length > 0 && newWinners.length > 0) {
          const latestId = newWinners[0]?.id;
          const wasAlreadyShown = previousWinnersRef.current.some(w => w.id === latestId);
          if (!wasAlreadyShown) {
            setLatestWinner(newWinners[0]);
            setShowCelebration(true);
            setTimeout(() => setShowCelebration(false), 5000);
          }
        }
        
        // Si pas assez de gagnants réels, compléter avec des fallback
        const finalWinners = newWinners.length >= 5 
          ? newWinners 
          : [...newWinners, ...generateFallbackWinners(8 - newWinners.length)];
        
        setWinners(finalWinners);
        setWeeklyCount(data.weeklyCount || finalWinners.length);
        previousWinnersRef.current = newWinners;
      } else {
        // Fallback en cas d'erreur API
        setWinners(generateFallbackWinners(10));
        setWeeklyCount(10);
      }
    } catch (error) {
      console.error('Erreur chargement gagnants:', error);
      // Fallback en cas d'erreur réseau
      setWinners(generateFallbackWinners(10));
      setWeeklyCount(10);
    } finally {
      setIsLoading(false);
    }
  };

  // Charger au montage puis rafraîchir toutes les 30 secondes
  useEffect(() => {
    fetchWinners();
    const interval = setInterval(fetchWinners, 30000);
    return () => clearInterval(interval);
  }, []);

  // Afficher un loader si chargement
  if (isLoading && winners.length === 0) {
    return (
      <div className="flex items-center justify-center p-4">
        <Trophy className="w-6 h-6 text-yellow-400 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden max-w-full">
      {/* ============== BANNIÈRE NÉON PRINCIPALE ============== */}
      <motion.div
        className="relative overflow-hidden rounded-2xl mb-4"
        style={{
          background: isDarkMode 
            ? 'linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)'
            : 'linear-gradient(135deg, #1a1a3e 0%, #2d1f5e 50%, #1a1a3e 100%)',
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Bordure animée néon */}
        <div 
          className="absolute inset-0 rounded-2xl"
          style={{
            background: 'linear-gradient(90deg, #FFD700, #FF6B6B, #A855F7, #06B6D4, #FFD700)',
            backgroundSize: '200% 100%',
            animation: 'gradientMove 3s linear infinite',
            padding: '2px',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }}
        />

        {/* Header avec étoiles */}
        <div className="relative px-4 py-2 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Trophy className="w-5 h-5 text-yellow-400" />
            </motion.div>
            <span className="text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400">
              GAGNANTS EN DIRECT
            </span>
            <motion.div
              className="w-2 h-2 rounded-full bg-green-400"
              animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          </div>
          
          {/* Compteur hebdomadaire */}
          <motion.div 
            className="flex items-center gap-1 px-2 py-1 rounded-full"
            style={{
              background: 'linear-gradient(135deg, rgba(255,215,0,0.2) 0%, rgba(255,107,107,0.2) 100%)',
              border: '1px solid rgba(255,215,0,0.3)'
            }}
          >
            <Sparkles className="w-3 h-3 text-yellow-400" />
            <span className="text-[10px] font-bold text-yellow-400">
              {weeklyCount} tampons cette semaine !
            </span>
          </motion.div>
        </div>

        {/* ============== TICKER DÉFILANT STYLE VEGAS ============== */}
        <div className="relative overflow-hidden py-2 px-2">
          <motion.div
            className="flex gap-3 whitespace-nowrap"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          >
            {[...winners, ...winners].map((winner, index) => {
              const game = GAME_NAMES[winner.game] || { name: 'Jeu', emoji: '🎮', color: '#6366F1' };
              return (
                <motion.div
                  key={`${winner.id}-${index}`}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${game.color}25 0%, ${game.color}10 100%)`,
                    border: `1px solid ${game.color}30`,
                  }}
                >
                  <span className="text-base">{game.emoji}</span>
                  <span className="text-xs font-bold text-white">{winner.name}</span>
                  <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                </motion.div>
              );
            })}
          </motion.div>
          
          {/* Dégradés de fondu sur les côtés */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[#0f0f23] to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#0f0f23] to-transparent pointer-events-none" />
        </div>
      </motion.div>

      {/* ============== NOTIFICATION POPUP NOUVEAU GAGNANT ============== */}
      <AnimatePresence>
        {showCelebration && latestWinner && (
          <motion.div
            className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50"
            initial={{ y: -100, opacity: 0, scale: 0.8 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -100, opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', damping: 15 }}
          >
            <div 
              className="relative px-6 py-4 rounded-2xl flex items-center gap-4"
              style={{
                background: 'linear-gradient(135deg, #1a1a3e 0%, #2d1f4e 100%)',
                border: '2px solid #FFD700',
                boxShadow: '0 0 40px rgba(255,215,0,0.5), 0 0 80px rgba(255,215,0,0.3), 0 20px 40px rgba(0,0,0,0.5)'
              }}
            >
              {/* Confettis animés */}
              <ConfettiExplosion />
              
              {/* Icône du jeu */}
              <motion.div
                className="text-4xl"
                animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5, repeat: Infinity }}
              >
                {GAME_NAMES[latestWinner.game].emoji}
              </motion.div>
              
              {/* Message */}
              <div className="flex flex-col">
                <motion.span
                  className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400"
                  animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
                  style={{ backgroundSize: '200%' }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  🎉 NOUVEAU GAGNANT ! 🎉
                </motion.span>
                <span className="text-white font-bold">
                  {latestWinner.name} vient de gagner un TAMPON !
                </span>
                <span className="text-sm text-gray-400">
                  au {GAME_NAMES[latestWinner.game].name}
                </span>
              </div>
              
              {/* Trophée animé */}
              <motion.div
                animate={{ y: [0, -5, 0], rotate: [0, 5, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <Crown className="w-8 h-8 text-yellow-400" />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Style CSS pour l'animation du gradient */}
      <style>{`
        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>
    </div>
  );
}

// ============== CONFETTI EXPLOSION ==============
// Positions pré-calculées pour les confettis (évite Math.random dans render)
const CONFETTI_POSITIONS = [
  { x: -120, y: 80, rotate: 240 },
  { x: 90, y: -40, rotate: 520 },
  { x: -80, y: 120, rotate: 180 },
  { x: 140, y: 60, rotate: 400 },
  { x: -50, y: -80, rotate: 620 },
  { x: 100, y: 100, rotate: 300 },
  { x: -140, y: 40, rotate: 480 },
  { x: 60, y: -60, rotate: 200 },
  { x: -100, y: 140, rotate: 560 },
  { x: 120, y: -20, rotate: 340 },
  { x: -60, y: 60, rotate: 440 },
  { x: 80, y: 140, rotate: 160 },
  { x: -130, y: -40, rotate: 600 },
  { x: 50, y: 80, rotate: 280 },
  { x: -90, y: -60, rotate: 500 },
  { x: 110, y: 40, rotate: 380 },
  { x: -40, y: 100, rotate: 220 },
  { x: 70, y: -80, rotate: 580 },
  { x: -110, y: 60, rotate: 420 },
  { x: 130, y: 120, rotate: 260 },
];

function ConfettiExplosion() {
  const confettiColors = ['#FFD700', '#FF6B6B', '#A855F7', '#06B6D4', '#22C55E', '#EC4899'];

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {CONFETTI_POSITIONS.map((pos, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 rounded-full"
          style={{
            background: confettiColors[i % confettiColors.length],
            left: '50%',
            top: '50%',
            boxShadow: `0 0 6px ${confettiColors[i % confettiColors.length]}`
          }}
          initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
          animate={{
            x: pos.x,
            y: pos.y,
            scale: [0, 1, 0.5],
            opacity: [1, 1, 0],
            rotate: pos.rotate
          }}
          transition={{
            duration: 1.5,
            ease: 'easeOut',
            delay: i * 0.02
          }}
        />
      ))}
    </div>
  );
}

// ============== MINI VERSION POUR LE HEADER ==============
export function WinnersMiniTicker({ className = '' }) {
  const [currentWinner, setCurrentWinner] = useState(0);
  const [winners, setWinners] = useState(() => generateFallbackWinners(5));

  // Charger les vrais gagnants
  useEffect(() => {
    const fetchWinners = async () => {
      try {
        const res = await fetch(`${API_URL}/api/games/recent-winners?limit=5`);
        if (res.ok) {
          const data = await res.json();
          if (data.winners && data.winners.length > 0) {
            setWinners(data.winners);
          }
        }
      } catch (error) {
        // Garder les fallback
      }
    };
    fetchWinners();
    const interval = setInterval(fetchWinners, 60000); // Rafraîchir toutes les minutes
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (winners.length === 0) return;
    const interval = setInterval(() => {
      setCurrentWinner(prev => (prev + 1) % winners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [winners.length]);

  if (winners.length === 0) return null;

  const winner = winners[currentWinner];
  const game = GAME_NAMES[winner.game] || { name: 'Jeu', emoji: '🎮', color: '#6366F1' };

  return (
    <motion.div 
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${className}`}
      style={{
        background: 'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(255,107,107,0.1) 100%)',
        border: '1px solid rgba(255,215,0,0.2)'
      }}
    >
      <motion.div
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
      >
        <Trophy className="w-4 h-4 text-yellow-400" />
      </motion.div>
      
      <AnimatePresence mode="wait">
        <motion.div
          key={currentWinner}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="flex items-center gap-1 text-xs"
        >
          <span className="text-yellow-400 font-bold">{winner.name}</span>
          <span className="text-gray-400">a gagné</span>
          <span>{game.emoji}</span>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}

// ============== COMPTEUR ANIMÉ POUR LA PAGE D'ACCUEIL ==============
export function WinnersCounter({ count = 42 }) {
  const [displayCount, setDisplayCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 2000;
    const increment = count / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= count) {
        setDisplayCount(count);
        clearInterval(timer);
      } else {
        setDisplayCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [count]);

  return (
    <motion.div
      className="relative inline-flex items-center gap-3 px-6 py-3 rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, #1a1a3e 0%, #2d1f4e 100%)',
        border: '2px solid rgba(255,215,0,0.3)',
        boxShadow: '0 0 30px rgba(255,215,0,0.2)'
      }}
      whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(255,215,0,0.4)' }}
    >
      <motion.div
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
      >
        <Gift className="w-8 h-8 text-yellow-400" />
      </motion.div>
      
      <div className="flex flex-col">
        <motion.span
          className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400"
          key={displayCount}
        >
          {displayCount}
        </motion.span>
        <span className="text-xs text-gray-400 font-medium">
          cadeaux gagnés cette semaine
        </span>
      </div>
      
      <motion.div
        className="absolute -top-1 -right-1"
        animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <Sparkles className="w-5 h-5 text-yellow-400" />
      </motion.div>
    </motion.div>
  );
}

export default WinnersShowcase;
